import React, { useState } from 'react';
import { Box, Typography, Grid, IconButton, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { getReviewsData } from '../../../utils/reviewsAdapter';
import { Link as LinkIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ExecutiveSummary from './ExecutiveSummary';
import G2Logo from 'assets/images/icons/g2.png';
import TrustpilotLogo from 'assets/images/icons/trustpilot-2.svg';

const OverallSummary = () => {
  // const [viewMode, setViewMode] = useState('bar');
  const [showLessTrusted, setShowLessTrusted] = useState(true);
  const [howToCollapsed, setHowToCollapsed] = useState(false);
  const reviewsState = useSelector((state) => state.reviewsReducer);
  const data = getReviewsData(reviewsState);
  const reviewSources = data.review_sources || {};

  const fakeReviews = data?.review_analysis_results?.suspected_fake_reviews;

  if (!data || !data.review_analysis_results?.overall_summary) {
    return null;
  }

  // const { overall_summary } = data.review_analysis_results;
  const totalReviews = data.rating_percentages.adjusted_counts.total;
  const positiveReviews = data.rating_percentages.adjusted_counts.positive;
  const negativeReviews = data.rating_percentages.adjusted_counts.negative;
  const positivePercentage = Math.round((positiveReviews / totalReviews) * 100);
  const negativePercentage = Math.round((negativeReviews / totalReviews) * 100);
  const averageRating = data.rating_percentages.average_rating;

  // Calculate fake reviews percentages
  const fakePositivePercentage = fakeReviews?.sentiment_breakdown?.positive
    ? Math.round((fakeReviews.sentiment_breakdown.positive / totalReviews) * 100)
    : 0;
  const fakeNeutralPercentage = fakeReviews?.sentiment_breakdown?.neutral
    ? Math.round((fakeReviews.sentiment_breakdown.neutral / totalReviews) * 100)
    : 0;
  const fakeNegativePercentage = fakeReviews?.sentiment_breakdown?.negative
    ? Math.round((fakeReviews.sentiment_breakdown.negative / totalReviews) * 100)
    : 0;

  const renderFakeReviewsTooltip = (sentiment) => {
    if (!fakeReviews) return null;

    const getSentimentSpecificInfo = () => {
      switch (sentiment) {
        case 'positive':
          return (
            <>
              <Typography variant="body2" gutterBottom>
                {fakeReviews.sentiment_breakdown.positive} positive reviews (
                {Math.round((fakeReviews.sentiment_breakdown.positive / totalReviews) * 100)}% of total) are suspected to be fake
              </Typography>
              <Typography variant="body2" gutterBottom>
                Examples of suspected fake positive reviews:
              </Typography>
              <Box sx={{ pl: 2 }}>
                {fakeReviews.detection_patterns
                  .filter((pattern) => pattern.sentiment === 'positive')
                  .map((pattern, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {pattern.pattern.charAt(0).toUpperCase() + pattern.pattern.slice(1)} ({pattern.frequency} reviews):
                      </Typography>
                      {pattern.examples.map((example, exIndex) => (
                        <Typography key={exIndex} variant="body2" sx={{ pl: 2, mb: 0.5 }}>
                          • &quot;{example}&quot;
                        </Typography>
                      ))}
                    </Box>
                  ))}
              </Box>
            </>
          );
        case 'negative':
          return (
            <>
              <Typography variant="body2" gutterBottom>
                {fakeReviews.sentiment_breakdown.negative} negative reviews (
                {Math.round((fakeReviews.sentiment_breakdown.negative / totalReviews) * 100)}% of total) are suspected to be fake
              </Typography>
              <Typography variant="body2" gutterBottom>
                Examples of suspected fake negative reviews:
              </Typography>
              <Box sx={{ pl: 2 }}>
                {fakeReviews.detection_patterns
                  .filter((pattern) => pattern.sentiment === 'negative')
                  .map((pattern, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {pattern.pattern.charAt(0).toUpperCase() + pattern.pattern.slice(1)} ({pattern.frequency} reviews):
                      </Typography>
                      {pattern.examples.map((example, exIndex) => (
                        <Typography key={exIndex} variant="body2" sx={{ pl: 2, mb: 0.5 }}>
                          • &quot;{example}&quot;
                        </Typography>
                      ))}
                    </Box>
                  ))}
              </Box>
            </>
          );
        case 'neutral':
          return (
            <>
              <Typography variant="body2" gutterBottom>
                {fakeReviews.sentiment_breakdown.neutral} neutral reviews (
                {Math.round((fakeReviews.sentiment_breakdown.neutral / totalReviews) * 100)}% of total) are suspected to be fake
              </Typography>
              <Typography variant="body2" gutterBottom>
                Common patterns in neutral fake reviews:
              </Typography>
              <Box sx={{ pl: 2 }}>
                {fakeReviews.detection_patterns
                  .filter((pattern) => pattern.sentiment === 'neutral')
                  .map((pattern, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {pattern.pattern.charAt(0).toUpperCase() + pattern.pattern.slice(1)} ({pattern.frequency} reviews):
                      </Typography>
                      {pattern.examples.map((example, exIndex) => (
                        <Typography key={exIndex} variant="body2" sx={{ pl: 2, mb: 0.5 }}>
                          • &quot;{example}&quot;
                        </Typography>
                      ))}
                    </Box>
                  ))}
              </Box>
            </>
          );
        default:
          return null;
      }
    };

    return (
      <Box sx={{ p: 1, maxWidth: 300 }}>
        <Typography variant="subtitle2" gutterBottom>
          Less trusted reviews
        </Typography>
        {getSentimentSpecificInfo()}
      </Box>
    );
  };

  // Custom tooltip for less trusted segments
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length && payload[0].payload.isFake && payload[0].payload.sentiment) {
      return (
        <Box sx={{ opacity: 1, bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1, boxShadow: 2, p: 2, minWidth: 220 }}>
          {renderFakeReviewsTooltip(payload[0].payload.sentiment)}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: 2, borderRadius: 1, mb: 3 }}>
      <Grid container spacing={3}>
        {/* Top Review Cards - MOVE TO TOP */}
        <Grid item xs={12}>
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, height: '100%' }}>
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" color="text.secondary">
                      Total Reviews
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      All collected feedback
                    </Typography>
                  </Box>

                  {/* Source icons in the top right */}
                  <Box sx={{ display: 'flex' }}>
                    {reviewSources.g2 && (
                      <IconButton
                        size="small"
                        href={`https://www.google.com/search?q=${encodeURIComponent(reviewSources.g2)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ p: 0.5, mr: 0.5 }}
                      >
                        <img src={G2Logo} alt="G2 Logo" style={{ width: 20, height: 20 }} />
                      </IconButton>
                    )}
                    {reviewSources.trustpilot && (
                      <IconButton
                        size="small"
                        href={`https://www.google.com/search?q=${encodeURIComponent(reviewSources.trustpilot)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ p: 0.5, mr: 0.5 }}
                      >
                        <img src={TrustpilotLogo} alt="Trustpilot Logo" style={{ width: 20, height: 20 }} />
                      </IconButton>
                    )}
                    {Object.entries(reviewSources)
                      .filter(([source]) => source !== 'g2' && source !== 'trustpilot')
                      .map(([source, url]) => (
                        <IconButton
                          key={source}
                          size="small"
                          href={`https://www.google.com/search?q=${encodeURIComponent(url)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            p: 0.5,
                            mr: 0.5,
                            textTransform: 'capitalize',
                            fontSize: '0.75rem'
                          }}
                        >
                          <LinkIcon sx={{ fontSize: '0.875rem' }} />
                          {source.slice(0, 1).toUpperCase()}
                        </IconButton>
                      ))}
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {totalReviews}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, height: '100%' }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Average Rating
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Overall satisfaction
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {averageRating.toFixed(1)}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      fontSize: '1.5rem',
                      lineHeight: 1
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        overflow: 'hidden',
                        width: `${(averageRating / 6) * 100}%`,
                        color: 'warning.main'
                      }}
                    >
                      ★
                    </Box>
                    <Box sx={{ color: 'grey.300' }}>★</Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  borderLeft: '4px solid',
                  borderColor: 'success.main',
                  height: '100%'
                }}
              >
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                  <Typography variant="subtitle1" color="text.secondary">
                    Positive Reviews
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Satisfied customers
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {positiveReviews}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'success.lighter',
                      color: 'success.dark',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'medium'
                    }}
                  >
                    {positivePercentage}%
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  borderLeft: '4px solid',
                  borderColor: 'error.main',
                  height: '100%'
                }}
              >
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                  <Typography variant="subtitle1" color="text.secondary">
                    Negative Reviews
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Areas for improvement
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h4" color="error.main" sx={{ fontWeight: 'bold' }}>
                    {negativeReviews}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'error.lighter',
                      color: 'error.dark',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'medium'
                    }}
                  >
                    {negativePercentage}%
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Executive Summary & Recommended Actions */}
        <Grid item xs={12}>
          <ExecutiveSummary />
        </Grid>

        {/* LEFT COLUMN: Review Sources & Sentiment Distribution */}
        <Grid
          item
          xs={12}
          md={howToCollapsed ? 12 : 8}
          sx={{
            position: 'relative',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Review Sources */}

          {/* Sentiment Distribution */}
          {data.rating_percentages?.sentiment && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h4" gutterBottom>
                Sentiment Distribution
              </Typography>

              <Box
                sx={{
                  height: howToCollapsed ? 450 : 350,
                  position: 'relative',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  mx: howToCollapsed ? 'auto' : 2,
                  maxWidth: howToCollapsed ? '900px' : 'none',
                  pb: 2
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: howToCollapsed ? 30 : 0,
                    zIndex: 20,
                    transition: 'right 0.5s ease-in-out'
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowLessTrusted(!showLessTrusted)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      py: 0.25,
                      px: 1,
                      minWidth: 'auto',
                      height: '24px',
                      borderColor: 'rgba(189, 189, 189, 0.3)',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'rgba(189, 189, 189, 0.5)',
                        backgroundColor: 'rgba(189, 189, 189, 0.1)'
                      }
                    }}
                  >
                    {showLessTrusted ? 'Hide Less Trusted' : 'Show Less Trusted'}
                  </Button>
                </Box>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    {/* Patterns for Less Trusted parts */}
                    <defs>
                      <linearGradient id="gradientPositive" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4caf50" stopOpacity={1} />
                        <stop offset="100%" stopColor="#81c784" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="gradientNeutral" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#9e9e9e" stopOpacity={1} />
                        <stop offset="100%" stopColor="#e0e0e0" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="gradientNegative" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f44336" stopOpacity={1} />
                        <stop offset="100%" stopColor="#e57373" stopOpacity={1} />
                      </linearGradient>
                      <pattern
                        id="patternLessTrustedPositive"
                        patternUnits="userSpaceOnUse"
                        width="4"
                        height="4"
                        patternTransform="rotate(45)"
                      >
                        <rect width="2" height="4" fill="url(#gradientPositive)" />
                        <rect x="2" width="2" height="4" fill="url(#gradientPositive)" fillOpacity="0.5" />
                      </pattern>
                      <pattern
                        id="patternLessTrustedNeutral"
                        patternUnits="userSpaceOnUse"
                        width="4"
                        height="4"
                        patternTransform="rotate(45)"
                      >
                        <rect width="2" height="4" fill="url(#gradientNeutral)" />
                        <rect x="2" width="2" height="4" fill="url(#gradientNeutral)" fillOpacity="0.5" />
                      </pattern>
                      <pattern
                        id="patternLessTrustedNegative"
                        patternUnits="userSpaceOnUse"
                        width="4"
                        height="4"
                        patternTransform="rotate(45)"
                      >
                        <rect width="2" height="4" fill="url(#gradientNegative)" />
                        <rect x="2" width="2" height="4" fill="url(#gradientNegative)" fillOpacity="0.5" />
                      </pattern>
                    </defs>

                    <Pie
                      data={[
                        {
                          name: 'Positive',
                          value:
                            showLessTrusted && fakePositivePercentage >= 3
                              ? data.rating_percentages.sentiment.positive - fakePositivePercentage
                              : data.rating_percentages.sentiment.positive,
                          color: '#4caf50'
                        },
                        ...(showLessTrusted && fakePositivePercentage >= 3
                          ? [
                              {
                                name: 'Less Trusted Positive',
                                value: fakePositivePercentage,
                                color: '#4caf50',
                                isFake: true,
                                sentiment: 'positive'
                              }
                            ]
                          : []),
                        {
                          name: 'Neutral',
                          value:
                            showLessTrusted && fakeNeutralPercentage >= 3
                              ? data.rating_percentages.sentiment.neutral - fakeNeutralPercentage
                              : data.rating_percentages.sentiment.neutral,
                          color: '#9e9e9e'
                        },
                        ...(showLessTrusted && fakeNeutralPercentage >= 3
                          ? [
                              {
                                name: 'Less Trusted Neutral',
                                value: fakeNeutralPercentage,
                                color: '#9e9e9e',
                                isFake: true,
                                sentiment: 'neutral'
                              }
                            ]
                          : []),
                        {
                          name: 'Negative',
                          value:
                            showLessTrusted && fakeNegativePercentage >= 3
                              ? data.rating_percentages.sentiment.negative - fakeNegativePercentage
                              : data.rating_percentages.sentiment.negative,
                          color: '#f44336'
                        },
                        ...(showLessTrusted && fakeNegativePercentage >= 3
                          ? [
                              {
                                name: 'Less Trusted Negative',
                                value: fakeNegativePercentage,
                                color: '#f44336',
                                isFake: true,
                                sentiment: 'negative'
                              }
                            ]
                          : [])
                      ].filter((item) => item.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={howToCollapsed ? 100 : 70}
                      outerRadius={howToCollapsed ? 150 : 100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => (value > 3 ? `${name}: ${value}%` : '')}
                      startAngle={90}
                      endAngle={-270}
                      isAnimationActive={false}
                      activeIndex={null}
                    >
                      {[
                        { name: 'Positive', color: 'url(#gradientPositive)' },
                        ...(showLessTrusted && fakePositivePercentage >= 3
                          ? [{ name: 'Less Trusted Positive', color: 'url(#gradientPositive)', pattern: true, sentiment: 'positive' }]
                          : []),
                        { name: 'Neutral', color: 'url(#gradientNeutral)' },
                        ...(showLessTrusted && fakeNeutralPercentage >= 3
                          ? [{ name: 'Less Trusted Neutral', color: 'url(#gradientNeutral)', pattern: true, sentiment: 'neutral' }]
                          : []),
                        { name: 'Negative', color: 'url(#gradientNegative)' },
                        ...(showLessTrusted && fakeNegativePercentage >= 3
                          ? [{ name: 'Less Trusted Negative', color: 'url(#gradientNegative)', pattern: true, sentiment: 'negative' }]
                          : [])
                      ]
                        .filter((entry, index) => index < (showLessTrusted ? 6 : 3))
                        .map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.pattern ? `url(#pattern${entry.name.replace(/\s+/g, '')})` : entry.color}
                          />
                        ))}
                    </Pie>
                    <RechartsTooltip
                      content={<CustomPieTooltip />}
                      wrapperStyle={{ position: 'fixed', zIndex: 21302, pointerEvents: 'auto', background: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>

                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    transition: 'all 0.5s ease-in-out'
                  }}
                >
                  <Typography variant={howToCollapsed ? 'h5' : 'h6'}>{totalReviews}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Reviews
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Expand button when collapsed */}
          {howToCollapsed && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '100%',
                width: 20,
                bgcolor: 'background.paper',
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                border: '1px solid #e0e0e0',
                borderLeft: 'none',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onClick={() => setHowToCollapsed(false)}
            >
              <ChevronLeft fontSize="small" />
            </Box>
          )}
        </Grid>

        {/* RIGHT COLUMN: How to Use Section with collapse controls */}
        <Grid
          item
          xs={12}
          md={howToCollapsed ? 0 : 4}
          sx={{
            display: howToCollapsed ? 'none' : 'block',
            position: 'relative',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <Box sx={{ display: 'flex', position: 'relative' }}>
            {/* Arrow section */}
            <Box
              sx={{
                position: 'absolute',
                left: -20,
                top: 0,
                height: '100%',
                width: 20,
                bgcolor: 'background.paper',
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,

                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderRadius: '16px',
                borderRight: 'none',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onClick={() => setHowToCollapsed(true)}
            >
              <ChevronRight fontSize="small" />
            </Box>

            {/* How to Use card content */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.paper',

                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderRadius: '16px',
                width: '100%'
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  How to Use This Report
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Actionable steps to leverage your review insights
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, p: 1, borderRadius: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.lighter',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'medium',
                      flexShrink: 0
                    }}
                  >
                    1
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      Identify Critical Issues
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Separate critical problems from minor complaints to focus on what really matters
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, px: 1, borderRadius: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.lighter',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'medium',
                      flexShrink: 0
                    }}
                  >
                    2
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      Leverage Social Proof
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use positive feedback in communication and marketing
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, p: 1, borderRadius: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.lighter',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'medium',
                      flexShrink: 0
                    }}
                  >
                    3
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      Prioritize Improvements
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Focus on most requested features and critical issues
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, px: 1, borderRadius: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.lighter',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'medium',
                      flexShrink: 0
                    }}
                  >
                    4
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      Examine the Customer Journey
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use the Customer Journey Map below to identify which stages of the customer experience are performing well and which
                      may require further attention or improvement.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverallSummary;
